import { Injectable } from '@angular/core';
import { User, Group } from '@microsoft/microsoft-graph-types-beta';
import { select, Store } from '@ngrx/store';
import {
    combineLatest,
    debounceTime,
    filter,
    iif,
    map,
    Observable,
    of,
    ReplaySubject,
    skipUntil,
    switchMap,
} from 'rxjs';
import { client } from 'projects/angular-clarity/src/app/stores/client';
import { selectSwaySpecAll, selectSwaySpecStatus } from 'projects/angular-clarity/src/app/stores/root.store';
import { loadDismissItems } from 'projects/angular-clarity/src/app/stores/client/octiga/dismiss/actions';
import { Dismiss } from 'projects/angular-clarity/src/app/stores/client/octiga/dismiss/model';
import { getRiskDismissId, UserRiskExclusion } from './../components/risks/model';

type TenantId = string;
type UserId = string;

/**
 * Single shared source of per-user account-risk data, consumed by both the Hardening
 * "Accounts Risks" page and the Dashboard "Account Risks" card. Manual risk dismissals
 * are overlaid here once so every consumer stays consistent instead of each one
 * re-deriving dismissed state independently.
 */
@Injectable({
    providedIn: 'root',
})
export class UserRisksService {
    private tenants: Map<TenantId, ReplaySubject<Map<UserId, UserRiskExclusion[]>>> = new Map();
    private dismissAwareRiskMaps: Map<TenantId, Observable<Map<UserId, UserRiskExclusion[]>>> = new Map();

    constructor(private store: Store) {}

    public getRiskMap(tenant_id: TenantId): Observable<Map<string, UserRiskExclusion[]>> {
        return this.getDismissAwareRiskMap(tenant_id);
    }

    public getAllRisks(tenant_id: TenantId): Observable<UserRiskExclusion[]> {
        return this.getDismissAwareRiskMap(tenant_id).pipe(map((risks) => [...risks.values()].flat(1)));
    }

    public getUserRisks(tenant_id: TenantId, user: User): Observable<UserRiskExclusion[]> {
        return this.getDismissAwareRiskMap(tenant_id).pipe(map((risks) => risks.get(user.id)));
    }

    private getDismissAwareRiskMap(tenant_id: TenantId): Observable<Map<UserId, UserRiskExclusion[]>> {
        if (this.dismissAwareRiskMaps.has(tenant_id)) {
            return this.dismissAwareRiskMaps.get(tenant_id);
        }

        const dismissItems$ = this.store.select(client(tenant_id).octiga.dismiss.all);
        const dismissAware$ = combineLatest([this.getSubject(tenant_id), dismissItems$]).pipe(
            map(([risksByUser, dismissItems]) => this.applyManualDismissals(tenant_id, risksByUser, dismissItems)),
        );

        this.dismissAwareRiskMaps.set(tenant_id, dismissAware$);
        return dismissAware$;
    }

    private applyManualDismissals(
        tenant_id: TenantId,
        risksByUser: Map<UserId, UserRiskExclusion[]>,
        dismissItems: Dismiss[],
    ): Map<UserId, UserRiskExclusion[]> {
        const dismissedIds = new Set(dismissItems.map((item) => item.id));
        const result = new Map<UserId, UserRiskExclusion[]>();

        for (const [userId, riskExclusions] of risksByUser) {
            result.set(
                userId,
                riskExclusions.map((riskExclusion) => {
                    if (riskExclusion.exclusion) return riskExclusion;

                    const dismissId = getRiskDismissId(tenant_id, riskExclusion.type, userId);
                    return dismissedIds.has(dismissId)
                        ? { ...riskExclusion, exclusion: { reason: 'Dismissed', dismissedManually: true } }
                        : riskExclusion;
                }),
            );
        }

        return result;
    }

    private getSubject(tenant_id: TenantId) {
        if (this.tenants.has(tenant_id)) {
            return this.tenants.get(tenant_id);
        } else {
            const subject = new ReplaySubject<Map<UserId, UserRiskExclusion[]>>(1);
            this.tenants.set(tenant_id, subject);
            this.subscribeToTenant(tenant_id);
            return subject;
        }
    }

    private subscribeToTenant(tenant_id: TenantId) {
        this.store.dispatch(loadDismissItems({ _tenant: tenant_id }));

        const worker = new Worker(new URL('./risks.worker', import.meta.url));

        worker.onmessage = ({ data }) => {
            this.tenants.get(tenant_id).next(data);
        };

        // users
        const usersReady$ = this.store.select(client(tenant_id).graph.users.status).pipe(map((res) => !!res.loaded));
        const users$ = this.store.select(client(tenant_id).graph.users.all).pipe(skipUntil(usersReady$));

        // mailboxes
        const mailboxesReady$ = this.store
            .select(client(tenant_id).powershell.exoMailbox.status)
            .pipe(map((res) => !!res.loaded));
        const mailboxes$ = this.store
            .select(client(tenant_id).powershell.exoMailbox.all)
            .pipe(skipUntil(mailboxesReady$));

        // role members
        const directoryRoleMembersReady$ = this.store
            .select(client(tenant_id).graph.directoryRole.members.status)
            .pipe(map((res) => !!res.loaded));
        const directoryRoleMembers$ = this.store
            .select(client(tenant_id).graph.directoryRole.members.all)
            .pipe(skipUntil(directoryRoleMembersReady$));

        // auth methods
        const authMethodsReady$ = this.store
            .select(client(tenant_id).graph.authenticationMethods.status)
            .pipe(map((res) => !!res.loaded));
        const authMethods$ = this.store
            .select(client(tenant_id).graph.authenticationMethods.all)
            .pipe(skipUntil(authMethodsReady$));

        // per User Mfa
        const perUserMfaStatus$ = this.store
            .select(client(tenant_id).graph.perUserMfaState.status)
            .pipe(map((res) => !!res.loaded));
        const perUserMfa$ = this.store
            .select(client(tenant_id).graph.perUserMfaState.all)
            .pipe(skipUntil(perUserMfaStatus$));

        const specsReady$ = this.store.select(selectSwaySpecStatus).pipe(map((res) => !!res.loaded));

        const specs$ = this.store.select(selectSwaySpecAll).pipe(skipUntil(specsReady$));

        const baselinesReady$ = this.store
            .select(client(tenant_id).sway.baselines.status)
            .pipe(map((res) => !!res.loaded));

        const baselines$ = this.store.select(client(tenant_id).sway.baselines.all).pipe(skipUntil(baselinesReady$));

        const swayGroupReady$ = this.store
            .select(client(tenant_id).sway.groups.status)
            .pipe(map((res) => !!res.loaded));

        const swayGroups$ = this.store.pipe(select(client(tenant_id).sway.groups.all), skipUntil(swayGroupReady$));

        const filteredGraphGroups$ = swayGroups$.pipe(
            switchMap((groups) =>
                iif(
                    () => groups.length > 0,
                    combineLatest(groups.map((group) => this.group_members(tenant_id, group.id))),
                    of([]),
                ),
            ),
        );

        const graphGroupsMembersReady$ = this.store
            .select(client(tenant_id).graph.groups.members.status)
            .pipe(map((res) => !!res.loaded));
        const graphGroups$ = this.store
            .select(client(tenant_id).graph.groups.all)
            .pipe(skipUntil(graphGroupsMembersReady$));

        const CAPoliciesReady$ = this.store
            .select(client(tenant_id).graph.conditionalAccessPolicy.status)
            .pipe(map((res) => !!res.loaded));

        const CAPolicies$ = this.store.select(client(tenant_id).graph.conditionalAccessPolicy.all).pipe(
            map((policies) =>
                policies.filter(
                    (policy) => policy.grantControls?.builtInControls?.includes('mfa') && policy.state === 'enabled',
                ),
            ),

            skipUntil(CAPoliciesReady$),
        );

        const securityDefaultsReady$ = this.store
            .select(client(tenant_id).graph.securityDefaults.status)
            .pipe(filter((res) => !!res.loaded));

        const securityDefaults$ = this.store
            .select(client(tenant_id).graph.securityDefaults.item)
            .pipe(skipUntil(securityDefaultsReady$));

        const deviationsReady$ = this.store
            .select(client(tenant_id).sway.deviations.status)
            .pipe(filter((res) => !!res.loaded));

        const deviations$ = this.store
            .select(client(tenant_id).sway.deviations.allActive)
            .pipe(skipUntil(deviationsReady$));

        combineLatest([
            users$,
            mailboxes$,
            directoryRoleMembers$,
            authMethods$,
            perUserMfa$,
            filteredGraphGroups$,
            baselines$,
            specs$,
            graphGroups$,
            CAPolicies$,
            securityDefaults$,
            deviations$,
        ])
            .pipe(debounceTime(200))
            .subscribe(
                ([
                    users,
                    mailboxes,
                    members,
                    authMethods,
                    perUserMfa,
                    filteredGraphGroups,
                    baselines,
                    specs,
                    graphGroups,
                    CAPolicies,
                    securityDefaults,
                    deviations,
                ]) => {
                    worker.postMessage([
                        users,
                        mailboxes,
                        members,
                        authMethods,
                        perUserMfa,
                        filteredGraphGroups,
                        baselines,
                        specs,
                        graphGroups,
                        tenant_id,
                        CAPolicies,
                        securityDefaults,
                        deviations,
                    ]);
                },
            );
    }

    private group_members(tenant_id: string, gid: string): Observable<Group> {
        if (tenant_id === gid) {
            // default group
            return this.store.pipe(select(client(tenant_id).graph.users.all)).pipe(
                filter((res) => res.length > 0),
                map((res) => ({ id: gid, members: res, displayName: 'Default' })),
            );
        } else {
            const group$ = this.store.pipe(select(client(tenant_id).graph.groups.group(gid)));
            const ready$ = this.store
                .select(client(tenant_id).graph.groups.members.status)
                .pipe(map((res) => !!res.loaded));
            const loaded$ = ready$.pipe(filter((loaded) => loaded));
            return group$.pipe(
                skipUntil(loaded$),
                map((group) => group ?? { id: gid, members: [], displayName: '' }),
            );
        }
    }
}
